/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { EventDispatcher } from '../../../../events';
import { Logger } from '../../../../logger';

const logger = Logger.getInstance();

interface SchNativeImpressionEvents {
  NativeImpression: any;
}

/**
 * Singleton listener for Sch publisher's custom DOM events
 * Listens to 'advertory:nativeImpression' events dispatched by the publisher
 */
class SchEventDispatcher extends EventDispatcher<SchNativeImpressionEvents> {
  private static instance: SchEventDispatcher | null = null;
  private isListening = false;
  private domEventHandler: ((event: CustomEvent) => void) | null = null;

  private constructor() {
    super();
  }

  public static getInstance(): SchEventDispatcher {
    if (!SchEventDispatcher.instance) {
      SchEventDispatcher.instance = new SchEventDispatcher();
    }
    return SchEventDispatcher.instance;
  }

  /**
   * Starts listening to 'advertory:nativeImpression' DOM events
   * Returns true if successfully started, false if already listening
   */
  public startListening(): boolean {
    if (this.isListening) {
      return true;
    }

    this.domEventHandler = (event: CustomEvent) => {
      logger.debug('advertory:nativeImpression event received');

      try {
        const adObj = event.detail?.ad;
        if (adObj) {
          this.dispatch('NativeImpression', adObj);
        } else {
          logger.warn('advertory:nativeImpression event missing ad data in event.detail');
        }
      } catch (e) {
        logger.error(`Error processing advertory:nativeImpression event: ${e}`);
      }
    };

    window.addEventListener('advertory:nativeImpression', this.domEventHandler as EventListener);
    this.isListening = true;
    logger.info('Started listening for advertory:nativeImpression DOM events');
    return true;
  }

  /**
   * Stops listening to DOM events (primarily for cleanup/testing)
   */
  public stopListening(): void {
    if (this.domEventHandler) {
      window.removeEventListener(
        'advertory:nativeImpression',
        this.domEventHandler as EventListener,
      );
      this.domEventHandler = null;
      this.isListening = false;
      logger.info('Stopped listening for advertory:nativeImpression DOM events');
    }
  }

  public getIsListening(): boolean {
    return this.isListening;
  }
}

export { SchEventDispatcher, type SchNativeImpressionEvents };
